import './style.scss';

import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { getAll as getAllPrograms } from 'apis/programs.api';
import { getDetailKpiStatistics, getYears } from 'apis/statistics.api';
// import { Can } from 'components/Can';
import { getProjectTypes, mapError } from 'utils/func';
import SearchFilters from 'features/Statistics/Components/SearchFilters';

export default function KpiStatistics() {
  // #region Data
  const location = useLocation();
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState(null);

  const [isVisibleSearchFilters, setIsVisibleSearchFilters] = useState(false);
  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    programYears: null,
    projectTypes: null,
    programNames: null,
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    programYears: null,
    projectTypes: null,
    programNames: null,
  });

  const projectTypes = getProjectTypes(t);

  const {
    data: _data,
    isLoading,
    isError,
    error,
  } = useQuery(['kpiStatistics', appliedFilters], async ({ queryKey: [, _filters] }) => {
    const result = await getDetailKpiStatistics(_filters);
    return result;
  });
  const data = useMemo(() => _data?.data ?? [], [_data]);

  const { data: programData } = useQuery(['program'], getAllPrograms);
  const programNames = useMemo(() => programData?.data ?? [], [programData]);

  const { data: yearData } = useQuery(['year'], getYears);
  const programYears = useMemo(() => yearData?.data ?? [], [yearData]);
  // #end region Data
  // #region Event

  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    setFilters({
      ...localFilters,
    });
    setAppliedFilters({
      ...localFilters,
    });
  }, []);

  const renderHeader = (
    <ColumnGroup>
      <Row>
        <Column header={t('researchKpiGroup.name')} rowSpan={2} />
        <Column header={t('projectProposal.notQualified')} colSpan={2} />
        <Column header={t('projectProposal.qualified')} colSpan={2} />
      </Row>
      <Row>
        <Column header={t('projectProposal.proposalQuantity')} />
        <Column header={t('projectProposal.kpiQuantity')} />
        <Column header={t('projectProposal.proposalQuantity')} />
        <Column header={t('projectProposal.kpiQuantity')} />
      </Row>
    </ColumnGroup>
  );

  // #end region Event
  // #endregion

  return (
    <div>
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('researchKpiGroup.viewList.title')}
        </h4>
      </div>

      <div className="flex justify-content-between mb-4">
        <div>
          <Button
            type="button"
            icon="pi pi-filter"
            label={t('projectProposal.filter')}
            outlined
            onClick={() => {
              setIsVisibleSearchFilters(true);
            }}
          />
        </div>
        {filters?.programYears?.length !== 0 && filters?.programYears !== null && (
          <div>
            <h4>{t('projectProposal.year')}</h4>
            <p className="display-current-filter">{filters?.programYears?.join(', ')}</p>
          </div>
        )}
        {filters?.projectTypes?.length !== 0 && filters?.projectTypes !== null && (
          <div>
            <h4>{t('projectProposal.viewList.projectType')}</h4>
            <p className="display-current-filter"> {filters?.projectTypes?.join(', ')}</p>
          </div>
        )}
        {filters?.programNames?.length !== 0 && filters?.programNames !== null && (
          <div>
            <h4>{t('projectProposal.viewList.program')}</h4>
            <p className="display-current-filter"> {filters?.programNames?.join(', ')}</p>
          </div>
        )}
      </div>

      <DataTable
        value={data}
        className="p-datatable-gridlines header-table"
        headerColumnGroup={renderHeader}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        dataKey="id"
        showGridlines
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('researchKpiGroup.viewList.emptyMessage')}
      >
        <Column field="name" className="w-18rem" style={{ minWidth: '10rem' }} />
        <Column
          field="notQualified.quantityProposal"
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
        <Column
          field="notQualified.quantityKpi"
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
        <Column
          field="qualified.quantityProposal"
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
        <Column
          field="qualified.quantityKpi"
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
      </DataTable>
      {isVisibleSearchFilters && (
        <SearchFilters
          visible={isVisibleSearchFilters}
          setVisible={setIsVisibleSearchFilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          projectTypes={projectTypes}
          programNames={programNames}
          programYears={programYears}
        />
      )}
    </div>
  );
}
